import type {
  MaterialCaptureType,
  MaterialCaptureUnit,
  MaterialPresentationType,
} from '../materials-catalog/index.js';
import type { CustomerIdentifierSnapshot, CustomerSnapshot } from '../customers/index.js';
import type { PurchaseSettlementSummary } from './purchases-settlement.dto.js';

export const purchaseIdentifierTypeValues = ['caId', 'plate'] as const;
export type PurchaseIdentifierType = (typeof purchaseIdentifierTypeValues)[number];

export const purchaseModeValues = ['production', 'training'] as const;
export type PurchaseMode = (typeof purchaseModeValues)[number];
export const purchaseOperatingModeValues = ['production', 'training'] as const;
export type PurchaseOperatingMode = (typeof purchaseOperatingModeValues)[number];
export const purchaseCompletionPrintActionValues = ['immediate', 'defer'] as const;
export type PurchaseCompletionPrintAction = (typeof purchaseCompletionPrintActionValues)[number];
export const purchaseReceiptModeValues = ['compact', 'detailed'] as const;
export type PurchaseReceiptMode = (typeof purchaseReceiptModeValues)[number];
export const purchaseReceiptCopyRoleValues = ['archive', 'customer'] as const;
export type PurchaseReceiptCopyRole = (typeof purchaseReceiptCopyRoleValues)[number];
export const purchaseReceiptCopyPrintStatusValues = [
  'printed',
  'pending',
  'deferred',
  'not_required',
] as const;
export type PurchaseReceiptCopyPrintStatus =
  (typeof purchaseReceiptCopyPrintStatusValues)[number];
export const purchaseCustomerReceiptCopyPolicyValues = [
  'automatic',
  'manual',
  'disabled',
] as const;
export type PurchaseCustomerReceiptCopyPolicy =
  (typeof purchaseCustomerReceiptCopyPolicyValues)[number];
export const purchasePrintAttemptOutcomeValues = [
  'printed',
  'failed',
  'outcome_unconfirmed',
] as const;
export type PurchasePrintAttemptOutcome = (typeof purchasePrintAttemptOutcomeValues)[number];

export const cashRoundingModeValues = ['symmetric', 'customer_favor', 'business_favor'] as const;
export type CashRoundingMode = (typeof cashRoundingModeValues)[number];

export const cashRoundingApplicationModeValues = ['automatic', 'optional', 'disabled'] as const;
export type CashRoundingApplicationMode = (typeof cashRoundingApplicationModeValues)[number];

export const purchasePrintStatusValues = ['printed', 'pending', 'deferred'] as const;
export type PurchasePrintStatus = (typeof purchasePrintStatusValues)[number];
export const purchasePrintStatusUpdateValues = ['printed', 'pending'] as const;
export type PurchasePrintStatusUpdate = (typeof purchasePrintStatusUpdateValues)[number];

export const purchaseReceiptFolioDocumentTypeValues = ['purchase_receipt'] as const;
export type PurchaseReceiptFolioDocumentType =
  (typeof purchaseReceiptFolioDocumentTypeValues)[number];
export const purchaseReceiptFolioScopeTypeValues = ['location'] as const;
export type PurchaseReceiptFolioScopeType = (typeof purchaseReceiptFolioScopeTypeValues)[number];
export const purchaseReceiptFolioSequenceStatusValues = ['active', 'inactive'] as const;
export type PurchaseReceiptFolioSequenceStatus =
  (typeof purchaseReceiptFolioSequenceStatusValues)[number];

export const purchaseAdjustmentKindValues = ['PROMOTION_BONUS', 'QUALITY_DISCOUNT'] as const;
export type PurchaseAdjustmentKind = (typeof purchaseAdjustmentKindValues)[number];

export const purchaseAdjustmentValueTypeValues = ['centsPerLb', 'percent'] as const;
export type PurchaseAdjustmentValueType = (typeof purchaseAdjustmentValueTypeValues)[number];

export interface StationContextRef {
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
}

export interface IdentifierInput {
  type: PurchaseIdentifierType;
  value: string;
  plateState?: string | null;
}

export interface IdentifierSnapshot {
  type: PurchaseIdentifierType;
  value: string;
  plateState: string | null;
  capturedAt: string; // ISO datetime
}

export interface PurchaseLinePricing {
  baseRatePerLb: number | null;
  premiumPerLb: number | null;
  effectiveRatePerLb: number | null;
  depositPerPiece: number | null;
  ratePerLb: number | null;
  contaminationPerLb: number | null;
  qualityDiscountPercent?: number | null;
  qualityDiscountMoneyCents?: number | null;
  promoCentsPerLb?: number | null;
  promoMoneyCents?: number | null;
}

export interface AdjustmentRecord {
  id: string;
  presetId?: string | null;
  presetCode?: string | null;
  presetLabel?: string | null;
  kind: PurchaseAdjustmentKind;
  valueType: PurchaseAdjustmentValueType;
  value: number;
  reasonCode?: string | null;
  reasonNote?: string | null;
  appliedByUserId?: string | null;
  appliedAt?: string | null; // ISO datetime
}

export interface PurchaseLineAdjustmentInput {
  kind: PurchaseAdjustmentKind;
  valueType: PurchaseAdjustmentValueType;
  value: number;
  reasonCode?: string | null;
  reasonNote?: string | null;
}

export interface PurchaseLineEntry {
  id: string;
  materialId: string;
  materialDisplayName: string;
  presentationType: MaterialPresentationType;
  captureType: MaterialCaptureType;
  unit: MaterialCaptureUnit;
  grossWeightLbs: number | null;
  tareWeightLbs: number | null;
  netWeightLbs: number;
  quantity: number | null;
  pricing: PurchaseLinePricing;
  adjustments?: AdjustmentRecord[];
  lineTotal: number;
  equivalentLbs: number;
  isCrvEligible: boolean;
  isReportedToDoriis: boolean;
}

export interface PurchaseLineInput {
  materialId: string;
  presentationType: MaterialPresentationType;
  grossWeightLbs?: number | null;
  tareWeightLbs?: number | null;
  quantity?: number | null;
}

export interface PurchaseTotals {
  grossWeightLbs: number;
  tareWeightLbs: number;
  netWeightLbs: number;
  lineSubtotal: number;
  roundingAdjustment: number;
  finalPayout: number;
  crvTotal: number;
  scrapTotal: number;
}

export interface PurchaseCashRoundingSnapshot {
  // `true` means the policy was active and evaluated, even if the money delta is zero.
  roundingApplied: boolean;
  roundingMode: CashRoundingMode;
  roundingIncrement: number;
  roundingAdjustment: number;
  roundingApplicationMode: CashRoundingApplicationMode;
  roundingPolicyVersion: number;
}

export interface PurchaseOperatorEmployeeSnapshot {
  employeeId: string;
  displayName: string;
  employeeNumber: string | null;
  storeId: string;
  locationId: string | null;
  capturedAt: string; // ISO datetime
  resolutionScope: 'company';
}

export interface PurchaseCashSessionOption {
  cashSessionId: string;
  cashLocationId: string;
  cashLocationName: string;
  currentBalanceMinor: number;
  openedAt: string; // ISO datetime
}

export type PurchaseCashSessionResolutionStatus = 'selected' | 'ambiguous' | 'unavailable';

export interface PurchaseCashSessionResolution {
  status: PurchaseCashSessionResolutionStatus;
  selectedCashSessionId: string | null;
  selectedCashLocationId: string | null;
  options: PurchaseCashSessionOption[];
}

export interface PurchaseDraftPayload {
  lineItems: PurchaseLineEntry[];
  identifier: IdentifierInput | null;
  cashRoundingRequested: boolean;
  // Optional customer attribution snapshots (independent from identifier input).
  customerId?: string | null;
  customerSnapshot?: CustomerSnapshot | null;
  identifierSnapshot?: CustomerIdentifierSnapshot | null;
  notes?: string | null;
}

export interface PurchaseDraft {
  id: string;
  station: StationContextRef;
  laneId: string;
  payload: PurchaseDraftPayload;
  totals: PurchaseTotals;
  createdAt: string;
  updatedAt: string;
}

export interface HeldPurchase {
  id: string;
  station: StationContextRef;
  laneId: string;
  payload: PurchaseDraftPayload;
  totals: PurchaseTotals;
  lockedByUserId: string | null;
  lockExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrintStatus {
  status: PurchasePrintStatus;
  printedAt: string | null;
  printCount: number;
  receiptCopyStatuses?: ReceiptCopyPrintStatus[];
}

export interface ReceiptCopyPrintStatus {
  copyRole: PurchaseReceiptCopyRole;
  status: PurchaseReceiptCopyPrintStatus;
  printedAt: string | null;
  printCount: number;
  required: boolean;
}

export interface PurchaseOfficialFolioSnapshot {
  officialFolio: string;
  folioSequenceId: string | null;
  folioSequenceScopeType: PurchaseReceiptFolioScopeType;
  folioSequenceScopeId: string;
  folioDisplayPrefix: string;
  folioSequenceNumber: number;
  folioPadding: number;
}

export interface PurchaseCommittedSnapshot {
  id: string;
  folioNumber: number;
  officialFolio?: string;
  officialFolioSnapshot?: PurchaseOfficialFolioSnapshot;
  station: StationContextRef;
  laneId: string;
  operatorUserId: string;
  mode: PurchaseMode;
  identifier: IdentifierSnapshot | null;
  // Optional customer attribution snapshots (independent from identifier input).
  customerId?: string | null;
  customerSnapshot?: CustomerSnapshot | null;
  identifierSnapshot?: CustomerIdentifierSnapshot | null;
  operatorEmployeeSnapshot: PurchaseOperatorEmployeeSnapshot;
  cashSessionId: string | null;
  cashLocationId: string | null;
  settlementSummary?: PurchaseSettlementSummary;
  lineItems: PurchaseLineEntry[];
  totals: PurchaseTotals;
  cashRounding: PurchaseCashRoundingSnapshot;
  occurredAt: string; // ISO datetime
  createdAt: string; // ISO datetime
}

export interface PurchaseModeSummary {
  station: StationContextRef;
  laneId: string;
  operatorUserId: string;
  mode: PurchaseMode;
  identifier: IdentifierSnapshot | null;
  lineItems: PurchaseLineEntry[];
  totals: PurchaseTotals;
  cashRounding: PurchaseCashRoundingSnapshot;
  occurredAt: string; // ISO datetime
}

export interface PurchaseCompanySettingsSnapshot {
  companyId: string;
  cashRoundingEnabled: boolean;
  cashRoundingIncrement: number;
  cashRoundingMode: CashRoundingMode;
  cashRoundingApplicationMode: CashRoundingApplicationMode;
  cashRoundingPolicyVersion: number;
}

export interface PurchaseSettingsSnapshot {
  station: StationContextRef;
  idRequiredPayoutThreshold: number;
  heldLockTtlMinutes: number;
  cancelGraceSeconds: number;
  mode: PurchaseOperatingMode;
  cashRoundingEnabled: boolean;
  cashRoundingIncrement: number;
  cashRoundingMode: CashRoundingMode;
  cashRoundingApplicationMode: CashRoundingApplicationMode;
  cashRoundingPolicyVersion: number;
  operationalTimeZone?: string;
  operationalTimeZoneSource?: 'location' | 'store' | 'company' | 'system';
}

export interface PurchaseCancellation {
  id: string;
  companyId: string;
  purchaseId: string;
  station: StationContextRef;
  reason: string;
  replaceWithPurchaseId: string | null;
  canceledByUserId: string;
  approvedByUserId: string | null;
  canceledAt: string; // ISO datetime
  createdAt: string; // ISO datetime
}

export interface PurchasePostCommitNote {
  id: string;
  companyId: string;
  station: StationContextRef;
  purchaseId: string | null;
  noteType: string;
  note: string;
  createdByUserId: string;
  createdAt: string; // ISO datetime
}

export interface PurchaseLimitLedgerEntry {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  operatorUserId: string;
  materialId: string;
  identifierType: PurchaseIdentifierType;
  identifierValue: string;
  plateState: string | null;
  occurredAt: string; // ISO datetime
  mode: PurchaseMode;
  totalCrvPaid: number;
  swRatePerLb: number;
  equivalentLbs: number;
  isReversal: boolean;
  reversalOfEntryId: string | null;
  purchaseId: string | null;
  createdAt: string; // ISO datetime
}

export interface PurchaseLimitLedgerEntryInput {
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  operatorUserId: string;
  materialId: string;
  identifierType: PurchaseIdentifierType;
  identifierValue: string;
  plateState: string | null;
  occurredAt: string; // ISO datetime
  mode: PurchaseMode;
  totalCrvPaid: number;
  swRatePerLb: number;
  equivalentLbs: number;
  isReversal: boolean;
  reversalOfEntryId: string | null;
  purchaseId: string | null;
}

export interface PurchaseCancellationSummary {
  reason: string;
  canceledByUserId: string;
  canceledAt: string; // ISO datetime
}

export interface PurchaseHistoryItem {
  id: string;
  folioNumber: number;
  officialFolio?: string;
  officialFolioSnapshot?: PurchaseOfficialFolioSnapshot;
  station: StationContextRef;
  operatorUserId: string;
  totalPayout: number;
  settlementSummary?: PurchaseSettlementSummary;
  occurredAt: string; // ISO datetime
  printStatus: PrintStatus;
  isCanceled: boolean;
  cancellationSummary?: PurchaseCancellationSummary | null;
}

export interface PurchaseReceiptFolioPreview {
  displayPrefix: string;
  padding: number;
  sequenceNumber: number;
  officialFolio: string;
}

export interface PurchaseReceiptFolioSequenceSnapshot {
  id: string;
  companyId: string;
  documentType: PurchaseReceiptFolioDocumentType;
  scopeType: PurchaseReceiptFolioScopeType;
  scopeId: string;
  storeId: string | null;
  locationId: string;
  stationId: string | null;
  displayPrefix: string;
  padding: number;
  nextNumber: number;
  lastIssuedNumber: number | null;
  lastIssuedAt: string | null;
  status: PurchaseReceiptFolioSequenceStatus;
  version: number;
  preview: PurchaseReceiptFolioPreview;
  editable: boolean;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string | null;
  updatedByUserId: string | null;
}
